import type { EngagementProvider } from "@/provider/EngagementProvider";
import type {
	EngagementProviderBootInput,
	ProviderRestartReason,
	ProviderRuntimeListener,
	ProviderRuntimeSnapshot,
	ProviderRuntimeState,
} from "@/provider/ProviderTypes";

type ProviderRuntimeCommand =
	| {
			type: "SET_PROVIDER_ID";
			payload: {
				providerId: string | null;
			};
	  }
	| {
			type: "SET_ELIGIBLE";
			payload: {
				providerId: string;
				bootKey: string;
			};
	  }
	| {
			type: "BOOT_STARTED";
			payload: {
				providerId: string;
				bootKey: string;
				processId: string;
			};
	  }
	| {
			type: "BOOT_SUCCEEDED";
			payload: {
				providerId: string;
				bootKey: string;
				processId: string;
			};
	  }
	| {
			type: "DEGRADED";
			payload: {
				providerId: string;
				bootKey: string | null;
				processId: string | null;
				reason: string;
			};
	  }
	| {
			type: "FAILED";
			payload: {
				providerId: string;
				bootKey: string | null;
				processId: string | null;
				reason: string;
				error: Error;
			};
	  }
	| {
			type: "STOPPED";
			payload: {
				reason: string;
			};
	  }
	| {
			type: "RESET";
	  };

function createInitialProviderRuntimeState(): ProviderRuntimeState {
	return {
		phase: "idle",
		processId: null,
		bootKey: null,
		providerId: null,
		reason: null,
		error: null,
	};
}

function createProcessId(providerId: string): string {
	const randomPart = Math.random().toString(36).slice(2);
	const timePart = Date.now().toString(36);

	return `${providerId}-${timePart}-${randomPart}`;
}

export function buildProviderBootKey(
	input: EngagementProviderBootInput,
	bootGeneration = 0
): string {
	return [
		input.integrationType ?? "unknown",
		input.sessionKey,
		input.serverUrl,
		input.gameName,
		input.lang,
		input.operator,
		input.platform,
		String(bootGeneration),
	].join("::");
}

function isRuntimeStoppedLike(
	state: ProviderRuntimeState
): state is Extract<
	ProviderRuntimeState,
	{ phase: "idle" | "stopped" | "failed" | "degraded" }
> {
	return (
		state.phase === "idle" ||
		state.phase === "stopped" ||
		state.phase === "failed" ||
		state.phase === "degraded"
	);
}

function isBootActivePhase(
	state: ProviderRuntimeState
): state is Extract<ProviderRuntimeState, { phase: "booting" | "ready" }> {
	return state.phase === "booting" || state.phase === "ready";
}

function canStopFromCurrentPhase(phase: ProviderRuntimeState["phase"]): boolean {
	return phase !== "idle";
}

function isSameRuntimeIdentity(
	state: ProviderRuntimeState,
	providerId: string,
	bootKey: string
): boolean {
	return state.providerId === providerId && state.bootKey === bootKey;
}

function resolveProviderId(
	input: EngagementProviderBootInput | null,
	providerId: string | null
): string | null {
	const integrationType = input?.integrationType?.trim();

	if (integrationType) {
		return integrationType;
	}

	return providerId;
}

export class ProviderRuntimeStore {
	private state: ProviderRuntimeState = createInitialProviderRuntimeState();
	private readonly listeners = new Set<ProviderRuntimeListener>();

	private providerId: string | null = null;
	private provider: EngagementProvider | null = null;
	private currentBootInput: EngagementProviderBootInput | null = null;

	public getSnapshot(): ProviderRuntimeSnapshot {
		return this.state;
	}

	public getProvider(): EngagementProvider | null {
		return this.provider;
	}

	public getProviderId(): string | null {
		return this.providerId;
	}

	public getCurrentBootInput(): EngagementProviderBootInput | null {
		return this.currentBootInput ? { ...this.currentBootInput } : null;
	}

	public subscribe(listener: ProviderRuntimeListener): () => void {
		this.listeners.add(listener);

		return () => {
			this.listeners.delete(listener);
		};
	}

	public setProviderId(providerId: string | null): void {
		if (providerId === this.providerId) {
			return;
		}

		this.providerId = providerId;
		this.provider = null;
		this.currentBootInput = null;

		if (!providerId) {
			this.dispatch({
				type: "RESET",
			});
			return;
		}

		const currentState = this.state;

		if (canStopFromCurrentPhase(currentState.phase)) {
			this.dispatch({
				type: "STOPPED",
				payload: {
					reason: "provider_replaced",
				},
			});
		}

		this.dispatch({
			type: "SET_PROVIDER_ID",
			payload: {
				providerId,
			},
		});
	}

	public setBootedProvider(provider: EngagementProvider | null): void {
		if (!provider) {
			this.provider = null;
			return;
		}

		if (!this.providerId) {
			throw new Error(
				"[ProviderRuntimeStore] cannot set a booted provider when providerId is missing"
			);
		}

		if (provider.id !== this.providerId) {
			throw new Error(
				`[ProviderRuntimeStore] booted provider id mismatch: expected ${this.providerId}, got ${provider.id}`
			);
		}

		this.provider = provider;
	}

	public markEligible(
		input: EngagementProviderBootInput,
		bootGeneration = 0
	): void {
		const resolvedProviderId = resolveProviderId(input, this.providerId);

		if (!resolvedProviderId) {
			throw new Error(
				"[ProviderRuntimeStore] cannot mark eligible without a resolved providerId"
			);
		}

		this.providerId = resolvedProviderId;
		this.currentBootInput = { ...input, integrationType: resolvedProviderId };

		const nextBootKey = buildProviderBootKey(
			this.currentBootInput,
			bootGeneration
		);
		const currentState = this.state;

		if (
			isSameRuntimeIdentity(currentState, resolvedProviderId, nextBootKey) &&
			(currentState.phase === "eligible" ||
				currentState.phase === "booting" ||
				currentState.phase === "ready")
		) {
			return;
		}

		if (
			currentState.providerId === resolvedProviderId &&
			currentState.bootKey !== null &&
			currentState.bootKey !== nextBootKey &&
			isBootActivePhase(currentState)
		) {
			this.provider = null;

			this.dispatch({
				type: "STOPPED",
				payload: {
					reason: "host_session_changed",
				},
			});
		}

		this.dispatch({
			type: "SET_ELIGIBLE",
			payload: {
				providerId: resolvedProviderId,
				bootKey: nextBootKey,
			},
		});
	}

	public beginBoot(bootGeneration = 0): void {
		if (!this.providerId || !this.currentBootInput) {
			throw new Error(
				"[ProviderRuntimeStore] cannot begin boot without providerId and boot input"
			);
		}

		const providerId = this.providerId;
		const bootKey = buildProviderBootKey(this.currentBootInput, bootGeneration);
		const currentState = this.state;

		if (
			currentState.phase === "booting" &&
			isSameRuntimeIdentity(currentState, providerId, bootKey)
		) {
			return;
		}

		const processId =
			currentState.phase === "booting" &&
			isSameRuntimeIdentity(currentState, providerId, bootKey)
				? currentState.processId
				: createProcessId(providerId);

		this.provider = null;

		this.dispatch({
			type: "BOOT_STARTED",
			payload: {
				providerId,
				bootKey,
				processId,
			},
		});
	}

	public markReady(bootGeneration = 0): void {
		if (!this.providerId || !this.currentBootInput) {
			throw new Error(
				"[ProviderRuntimeStore] cannot mark ready without providerId and boot input"
			);
		}

		const providerId = this.providerId;
		const bootKey = buildProviderBootKey(this.currentBootInput, bootGeneration);
		const currentState = this.state;

		if (
			currentState.phase === "ready" &&
			isSameRuntimeIdentity(currentState, providerId, bootKey)
		) {
			return;
		}

		const processId =
			isBootActivePhase(currentState) &&
			isSameRuntimeIdentity(currentState, providerId, bootKey)
				? currentState.processId
				: createProcessId(providerId);

		this.dispatch({
			type: "BOOT_SUCCEEDED",
			payload: {
				providerId,
				bootKey,
				processId,
			},
		});
	}

	public markFailed(reason: string, error: Error): void {
		if (!this.providerId) {
			throw new Error(
				"[ProviderRuntimeStore] cannot mark failed without providerId"
			);
		}

		this.provider = null;

		this.dispatch({
			type: "FAILED",
			payload: {
				providerId: this.providerId,
				bootKey: this.state.bootKey,
				processId: this.state.processId,
				reason,
				error,
			},
		});
	}

	public shutdown(reason: string): void {
		this.provider = null;

		this.dispatch({
			type: "STOPPED",
			payload: {
				reason,
			},
		});
	}

	public restart(reason: ProviderRestartReason, bootGeneration = 0): void {
		this.provider = null;

		this.dispatch({
			type: "STOPPED",
			payload: {
				reason,
			},
		});

		if (!this.currentBootInput || !this.providerId) {
			return;
		}

		this.markEligible(
			{
				...this.currentBootInput,
				integrationType: this.providerId,
			},
			bootGeneration
		);
	}

	public markDegraded(reason: string): void {
		if (!this.providerId) {
			throw new Error(
				"[ProviderRuntimeStore] cannot mark degraded without providerId"
			);
		}

		this.dispatch({
			type: "DEGRADED",
			payload: {
				providerId: this.providerId,
				bootKey: this.state.bootKey,
				processId: this.state.processId,
				reason,
			},
		});
	}

	public reset(): void {
		this.providerId = null;
		this.provider = null;
		this.currentBootInput = null;

		this.dispatch({
			type: "RESET",
		});
	}

	private dispatch(command: ProviderRuntimeCommand): void {
		const prevState = this.state;
		const nextState = this.reduce(this.state, command);

		if (nextState === this.state) {
			return;
		}

		this.state = nextState;

		for (const listener of this.listeners) {
			listener(nextState, prevState);
		}
	}

	private reduce(
		currentState: ProviderRuntimeState,
		command: ProviderRuntimeCommand
	): ProviderRuntimeState {
		switch (command.type) {
			case "SET_PROVIDER_ID": {
				if (!command.payload.providerId) {
					return createInitialProviderRuntimeState();
				}

				if (
					currentState.phase === "stopped" &&
					currentState.providerId === command.payload.providerId &&
					currentState.reason === "provider_selected"
				) {
					return currentState;
				}

				return {
					phase: "stopped",
					processId: null,
					bootKey: null,
					providerId: command.payload.providerId,
					reason: "provider_selected",
					error: null,
				};
			}

			case "SET_ELIGIBLE": {
				if (
					currentState.phase === "eligible" &&
					isSameRuntimeIdentity(
						currentState,
						command.payload.providerId,
						command.payload.bootKey
					)
				) {
					return currentState;
				}

				return {
					phase: "eligible",
					processId: null,
					bootKey: command.payload.bootKey,
					providerId: command.payload.providerId,
					reason: null,
					error: null,
				};
			}

			case "BOOT_STARTED": {
				if (
					currentState.phase === "booting" &&
					currentState.processId === command.payload.processId &&
					isSameRuntimeIdentity(
						currentState,
						command.payload.providerId,
						command.payload.bootKey
					)
				) {
					return currentState;
				}

				return {
					phase: "booting",
					processId: command.payload.processId,
					bootKey: command.payload.bootKey,
					providerId: command.payload.providerId,
					reason: null,
					error: null,
				};
			}

			case "BOOT_SUCCEEDED": {
				if (
					currentState.phase === "ready" &&
					currentState.processId === command.payload.processId &&
					isSameRuntimeIdentity(
						currentState,
						command.payload.providerId,
						command.payload.bootKey
					)
				) {
					return currentState;
				}

				return {
					phase: "ready",
					processId: command.payload.processId,
					bootKey: command.payload.bootKey,
					providerId: command.payload.providerId,
					reason: null,
					error: null,
				};
			}

			case "DEGRADED": {
				if (
					currentState.phase === "degraded" &&
					currentState.processId === command.payload.processId &&
					currentState.bootKey === command.payload.bootKey &&
					currentState.providerId === command.payload.providerId &&
					currentState.reason === command.payload.reason
				) {
					return currentState;
				}

				return {
					phase: "degraded",
					processId: command.payload.processId,
					bootKey: command.payload.bootKey,
					providerId: command.payload.providerId,
					reason: command.payload.reason,
					error: null,
				};
			}

			case "FAILED": {
				if (
					currentState.phase === "failed" &&
					currentState.processId === command.payload.processId &&
					currentState.bootKey === command.payload.bootKey &&
					currentState.providerId === command.payload.providerId &&
					currentState.reason === command.payload.reason &&
					currentState.error.message === command.payload.error.message
				) {
					return currentState;
				}

				return {
					phase: "failed",
					processId: command.payload.processId,
					bootKey: command.payload.bootKey,
					providerId: command.payload.providerId,
					reason: command.payload.reason,
					error: command.payload.error,
				};
			}

			case "STOPPED": {
				if (
					currentState.phase === "stopped" &&
					currentState.reason === command.payload.reason
				) {
					return currentState;
				}

				return {
					phase: "stopped",
					processId: currentState.processId,
					bootKey: currentState.bootKey,
					providerId: currentState.providerId,
					reason: command.payload.reason,
					error: null,
				};
			}

			case "RESET":
				return createInitialProviderRuntimeState();
		}
	}
}

let singletonProviderRuntimeStore: ProviderRuntimeStore | null = null;

export function getProviderRuntimeStore(): ProviderRuntimeStore {
	if (!singletonProviderRuntimeStore) {
		singletonProviderRuntimeStore = new ProviderRuntimeStore();
	}

	return singletonProviderRuntimeStore;
}